import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Container, Stack, TextField } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import { reportingPeriodCaptionsMap } from "@entrilia/shared/utilities/enumCaptions";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestTemplateVersion } from "../../../../api/types/dataCollectionTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import InlinePropertyBox from "../../../common/InlinePropertyBox";
import AddCompaniesDialog from "./AddCompaniesDialog";
import DataRequestTemplateCompaniesGrid from "./DataRequestTemplateCompaniesGrid";
import DataRequestTemplatePageTitle from "./DataRequestTemplatePageTitle";
import DataRequestTemplateRecipientFields from "./DataRequestTemplateRecipientFields";
import type { EditDataRequestTemplateState } from "./editDataRequestTemplateState";
import {
  getInitialEditDataRequestTemplateState,
  validateEditDataRequestTemplate,
} from "./editDataRequestTemplateState";

const createTemplateVersion = withErrorHandling(adminApi.createDataRequestTemplateVersion);

const getConfigurationSettingsProperties = (
  template: DataCollectionRequestTemplateVersion
): Array<[string, string]> => [
  ["Configuration", template.dataSubmissionConfigurationName || "Unknown"],
  ["Scenario", template.isScenarioConfigurable ? "N/A (configurable)" : (template.scenarioName ?? "")],
  ["Period", reportingPeriodCaptionsMap[template.reportingPeriod]],
];

const DataRequestTemplatePage = () => {
  usePageTitle("Data Request Template");

  const { templateId } = useParams();
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [state, setState] = useState<EditDataRequestTemplateState>(getInitialEditDataRequestTemplateState);
  const [recipientObjectIds, setRecipientObjectIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [isAddCompaniesDialogOpen, setIsAddCompaniesDialogOpen] = useState(false);

  const getTemplate = useCallback(() => adminApi.getLatestRequestTemplateVersion(defined(templateId)), [templateId]);

  const [template, fetchTemplateError, { setData: setTemplate }] = useFetch(getTemplate, {
    onSetData: (tpl) => {
      setState(getInitialEditDataRequestTemplateState(tpl));
      setRecipientObjectIds(tpl.recipientObjectIds);
    },
  });

  const [categories, fetchCategoriesError] = useFetch(adminApi.getObjectAccessCategories);

  const getPortfolioCompanyDefinition = useCallback(() => adminApi.getObjectDefinition("PortfolioCompany"), []);
  const [portfolioCompanyDefinition, fetchDefinitionError] = useFetch(getPortfolioCompanyDefinition);

  const getCompanies = useCallback(() => adminApi.getObjectsAsLookupReferences("PortfolioCompany"), []);
  const [companies, fetchCompaniesError] = useFetch(getCompanies);

  const [templates, fetchTemplatesError] = useFetch(adminApi.getDataRequestTemplates);

  const selectedCompanies = useMemo(
    () => (companies ?? []).filter((o) => recipientObjectIds.includes(o.objectId)),
    [recipientObjectIds, companies]
  );

  const companyOptions = useMemo(
    () => (companies ?? []).filter((o) => !recipientObjectIds.includes(o.objectId)),
    [recipientObjectIds, companies]
  );

  const fetchError =
    fetchTemplateError || fetchCategoriesError || fetchDefinitionError || fetchCompaniesError || fetchTemplatesError;

  if (fetchError) {
    logError(fetchError, "[DataRequestTemplatePage] fetch");
    return <DataLoadingFailed title="Could not load data request template" />;
  }

  if (
    template === undefined ||
    categories === undefined ||
    portfolioCompanyDefinition === undefined ||
    companies === undefined ||
    templates === undefined
  ) {
    return <InlineLoader />;
  }

  const objectDefinition = portfolioCompanyDefinition.objectClassDefinition;
  const hasPermissionsToManage = hasPermissions(["ManagePortfolioMonitoring"]);

  const existingTemplateNames = templates.filter((t) => t.templateId !== template.templateId).map((t) => t.name);
  const validation = validateEditDataRequestTemplate(state, existingTemplateNames);

  const initial = getInitialEditDataRequestTemplateState(template);
  const isDirty =
    state.name.trim() !== initial.name ||
    state.auditory !== initial.auditory ||
    state.categoryId !== initial.categoryId ||
    !arraysHaveSameItems(recipientObjectIds, template.recipientObjectIds);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleRecipientFieldsChange = (changes: Pick<EditDataRequestTemplateState, "auditory" | "categoryId">) => {
    setState((prev) => ({ ...prev, ...changes }));
  };

  const handleSave = async () => {
    if (!state.auditory) {
      return;
    }

    setSaving(true);

    const [resp, error] = await createTemplateVersion({
      templateId: template.templateId,
      objectType: "PortfolioCompany",
      configurationId: template.dataSubmissionConfigurationId,
      name: state.name,
      auditory: state.auditory,
      objectAccessCategoryIds: [state.categoryId],
      recipientObjectIds: recipientObjectIds.filter((id) => companies.some((c) => c.objectId === id)), // filter out any companies that have been removed
    });

    setSaving(false);

    if (error) {
      logError(error, "[DataRequestTemplatePage] save");
      sendNotificationError(error.message);
      return;
    }

    if (!resp.success) {
      logError(fetchError, "[DataRequestTemplatePage] save");
      sendNotificationError("Could not save template version:\n" + resp.errors.join("\n"));
      return;
    }

    sendNotification("Template version saved");
    setTemplate(resp.templateVersion);
    setState(getInitialEditDataRequestTemplateState(resp.templateVersion));
    setRecipientObjectIds(resp.templateVersion.recipientObjectIds);
  };

  const handleReset = () => {
    setState(getInitialEditDataRequestTemplateState(template));
    setRecipientObjectIds(template.recipientObjectIds);
  };

  const handleAddCompanies = (companyIds: string[]) => {
    setRecipientObjectIds((ids) => [...ids, ...companyIds]);
  };

  const handleRemoveCompanies = (companyIds: string[]) => {
    setRecipientObjectIds((ids) => ids.filter((id) => !companyIds.includes(id)));
  };

  return (
    <>
      <GeneralPageHeader
        TitleComponent={<DataRequestTemplatePageTitle template={template} />}
        showDefaultBackButtonTo={`/${clientCode}/${pageRoutes.portfolio}/${pageRoutes.dataRequestSetup}?tab=templates`}
      />
      <Container maxWidth="lg" sx={{ py: 2.5, height: "100%" }}>
        <Stack spacing={3} sx={{ height: "100%" }}>
          <TextField
            disabled={!hasPermissionsToManage}
            fullWidth
            value={state.name}
            onChange={handleNameChange}
            label="Name"
            error={!validation.nameResult.isValid}
            helperText={validation.nameResult.error}
          />

          <DataRequestTemplateRecipientFields
            auditory={state.auditory}
            categoryId={state.categoryId}
            categories={categories}
            objectDefinition={objectDefinition}
            onChange={handleRecipientFieldsChange}
            disabled={!hasPermissionsToManage}
          />

          <InlinePropertyBox title="Configuration Settings" properties={getConfigurationSettingsProperties(template)} />

          {hasPermissionsToManage && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="text" onClick={() => setIsAddCompaniesDialogOpen(true)} startIcon={<AddIcon />}>
                Add companies
              </Button>

              <Stack direction="row" spacing={1}>
                <Button
                  loading={saving}
                  variant="contained"
                  onClick={handleSave}
                  disabled={!isDirty || !validation.isValid}
                >
                  Save
                </Button>
                <Button variant="text" color="secondary" onClick={handleReset} disabled={!isDirty}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}

          <DataRequestTemplateCompaniesGrid
            rows={selectedCompanies}
            hasPermissionsToManage={hasPermissionsToManage}
            onRemoveCompanies={handleRemoveCompanies}
          />
        </Stack>
      </Container>
      {isAddCompaniesDialogOpen && (
        <AddCompaniesDialog
          companyOptions={companyOptions}
          onClose={() => setIsAddCompaniesDialogOpen(false)}
          onAdd={handleAddCompanies}
        />
      )}
    </>
  );
};

export default DataRequestTemplatePage;
